/** Extracts the hash fragment from the provided path and returns both the cleaned path and the hash. */
export function extractAndRemoveHash(path: string): {
  pathWithoutHash: string;
  hashFragment: string;
} {
  const hashIndex = path.indexOf('#');
  if (hashIndex !== -1) {
    return {
      pathWithoutHash: path.substring(0, hashIndex),
      hashFragment: path.substring(hashIndex + 1),
    };
  }
  return { pathWithoutHash: path, hashFragment: '' };
}

/**
 * Serializes Crystallize PropertiesTableContent sections into a URL query string.
 * Skips entries with missing/empty key or value.
 */
export function buildQueryStringFromPropertiesTable(
  sections?: ReadonlyArray<
    { properties?: Array<{ key?: string | null; value?: string | null }> | null } | null | undefined
  > | null,
): string {
  if (!sections) {
    return '';
  }

  const searchParams = new URLSearchParams();
  sections.forEach((section) => {
    section?.properties?.forEach((property) => {
      if (property.key && property.value) {
        searchParams.append(property.key, property.value);
      }
    });
  });

  return searchParams.toString();
}

/**
 * Appends a pre-built query string to a URL, preserving any existing query string and hash
 * fragment. Returns the href unchanged for mailto: links or when the query string is empty.
 */
export function appendQueryStringToUrl(href: string, queryString: string): string {
  if (!queryString || href.startsWith('mailto:')) {
    return href;
  }

  const { pathWithoutHash, hashFragment } = extractAndRemoveHash(href);
  const separator = pathWithoutHash.includes('?') ? '&' : '?';
  const fragment = hashFragment ? `#${hashFragment}` : '';

  return `${pathWithoutHash}${separator}${queryString}${fragment}`;
}

/**
 * Unwraps a Crystallize PropertiesTableContent component and appends its key/value pairs as
 * a query string to the given href.
 */
export function appendPropertiesTableComponentToUrl(
  href: string,
  component?: {
    content?: { __typename?: string | null } | null;
  } | null,
): string {
  const content = component?.content;
  const sections =
    content?.__typename === 'PropertiesTableContent'
      ? (content as { sections?: Parameters<typeof buildQueryStringFromPropertiesTable>[0] })
          .sections
      : null;

  return appendQueryStringToUrl(href, buildQueryStringFromPropertiesTable(sections));
}

export function slugify(str: string): string {
  return str
    .toLowerCase()
    .normalize('NFKD') // Normalize accented characters
    .replace(/[^\w\s-]/g, '-') // Replace non-word chars with hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Collapse multiple hyphens
    .replace(/^-+|-+$/g, ''); // Trim starting and ending hyphens
}

const SAFE_URL_SCHEMES = new Set(['http', 'https', 'mailto', 'tel']);

/**
 * Guards against script-execution URL schemes (javascript:, data:, vbscript:, ...) that content
 * editors could otherwise inject through a CMS rich-text link.
 * Relative paths, query strings and hash fragments carry no scheme and stay allowed.
 */
export function isSafeHref(href?: string | null): boolean {
  if (!href) {
    return false;
  }

  // Browsers strip control characters and whitespace before they parse the scheme, so a href
  // such as 'java\nscript:alert(1)' would still execute. Remove them before the check.
  // eslint-disable-next-line no-control-regex -- removing control characters is the point
  const normalized = href.replaceAll(/[\u0000-\u0020\u007f]/g, '');
  if (normalized.length === 0) {
    return false;
  }

  const schemeMatch = /^([a-z][a-z0-9+.-]*):/i.exec(normalized);
  if (!schemeMatch) {
    // No scheme means a relative path, a query string or a hash fragment.
    return true;
  }

  return SAFE_URL_SCHEMES.has(schemeMatch[1].toLowerCase());
}

// A path on this site, or a query on the current page ("?nuFlow=...");
// "//host/..." is another site without a scheme
export function isInternalPath(href: string): boolean {
  return (href.startsWith('/') && !href.startsWith('//')) || href.startsWith('?');
}

export function buildMailtoLink(subject: string, body: string): string {
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
